import React from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import type { Slot, PollInsights } from "../../lib/insights/types";
import type { Participant } from "./ResultsTable";
import DayPager from "./DayPager";
import SlotCard from "./SlotCard";
import ResultsModeTabs from "./ResultsModeTabs";
import ThresholdSegmented from "./ThresholdSegmented";
import ConsensusBanner from "./ConsensusBanner";
import PendingParticipants from "./PendingParticipants";
import BlockerCallout from "./BlockerCallout";
import { topDayOf, dayScores } from "../../lib/insights/dayScores";
import { formatDate, formatTime } from "../voting/utils/dateUtils";
import type { ResultsMode } from "../../hooks/useResultsMode";
import type { ConsensusFilterResult } from "../../lib/insights/consensusFilter";
import type { EngagementPartition } from "../../lib/insights/engagement";
import type { NearMissSlot } from "../../lib/insights/blockers";

interface MobileResultsProps {
  slots: Slot[];
  participants: Participant[];
  insights: PollInsights;
  mode: ResultsMode;
  onChangeMode: (m: ResultsMode) => void;
  threshold: number;
  onChangeThreshold: (n: number) => void;
  consensus: ConsensusFilterResult | null;
  engagement: EngagementPartition | null;
  nearMisses: NearMissSlot[];
  slotById: Map<string, Slot>;
  isShared: (participantId: string) => boolean;
  onCopyLink: (participantId: string) => void;
  onCopyShareLink: () => void;
}

/**
 * Mobile Pattern A : un jour à la fois, swipeable. Cf. Results Final.html
 * lignes 413-552. Default = jour le plus populaire (topDayOf).
 *
 * Modes (tableau/consensus/engagement/blockers) partagés avec le desktop
 * (cf. ResultsTableV2Section) — pilotés par le même state (mode/threshold
 * dans l'URL), simplement rendus en pills scrollables faute de place pour
 * des tabs. Cf. design canvas mobile-results-modes (2026-09-09).
 */
const MobileResults: React.FC<MobileResultsProps> = ({
  slots,
  participants,
  insights,
  mode,
  onChangeMode,
  threshold,
  onChangeThreshold,
  consensus,
  engagement,
  nearMisses,
  slotById,
  isShared,
  onCopyLink,
  onCopyShareLink,
}) => {
  const { t } = useTranslation("polls");
  const topDay = React.useMemo(
    () => topDayOf(slots, insights),
    [slots, insights],
  );
  const days = React.useMemo(
    () => dayScores(slots, insights),
    [slots, insights],
  );
  const [activeDay, setActiveDay] = React.useState<string>(
    topDay ?? days[0]?.date ?? "",
  );

  // Si activeDay devient invalide (slots changent), reset au top day
  React.useEffect(() => {
    if (!days.find((d) => d.date === activeDay) && days.length > 0) {
      setActiveDay(topDay ?? days[0].date);
    }
  }, [activeDay, days, topDay]);

  const daySlots = React.useMemo(() => {
    return slots.filter((s) => s.primaryDate === activeDay);
  }, [slots, activeDay]);

  const activeSlots =
    mode === "consensus" && consensus
      ? daySlots.filter((s) => consensus.visibleSlotIds.has(s.id))
      : daySlots;

  const cardParticipants =
    mode === "engagement" && engagement
      ? engagement.active
          .map((r) => participants.find((p) => p.id === r.id))
          .filter((p): p is Participant => !!p)
      : participants;

  const highlightedSlotIds =
    mode === "blockers" ? new Set(nearMisses.map((nm) => nm.slotId)) : null;
  const dimmedSlotIds =
    mode === "blockers"
      ? new Set(
          slots
            .filter(
              (sl) =>
                sl.id !== insights.winnerSlotId &&
                !nearMisses.some((nm) => nm.slotId === sl.id),
            )
            .map((sl) => sl.id),
        )
      : null;
  const dayNearMisses =
    mode === "blockers"
      ? nearMisses.filter(
          (nm) => slotById.get(nm.slotId)?.primaryDate === activeDay,
        )
      : [];

  const winnerId = insights.winnerSlotId;
  const winnerSlot = winnerId ? slots.find((s) => s.id === winnerId) : null;
  const winnerScore = winnerId
    ? insights.scores.find((s) => s.slotId === winnerId)
    : null;

  return (
    <div data-testid="mobile-results" className="flex flex-col gap-2.5 min-h-0">
      {/* Winner mini-banner */}
      {winnerSlot && winnerScore && (
        <div
          data-testid="mobile-winner-banner"
          className="mx-3 flex min-w-0 items-center gap-2 rounded-[9px] border border-vote-available/40 bg-vote-available/[0.10] px-2.5 py-2 text-[11px]"
        >
          <Star
            className="h-3 w-3 shrink-0 text-vote-available-foreground fill-vote-available-foreground"
            aria-hidden
          />
          <span className="shrink-0 text-muted-foreground">
            {t("results.mobile.leading")}{" "}
          </span>
          <span className="min-w-0 truncate font-semibold text-foreground">
            {formatDate(winnerSlot.primaryDate)}
            {winnerSlot.timeSlots && <> · {formatTime(winnerSlot.timeSlots)}</>}
          </span>
          <span className="ml-auto shrink-0 font-bold text-vote-available-foreground tabular-nums">
            {winnerScore.yes}/{insights.participantsCount}
          </span>
        </div>
      )}

      <DayPager
        slots={slots}
        insights={insights}
        activeDay={activeDay}
        onChange={setActiveDay}
        topDay={topDay}
      />

      <div className="-mx-1 px-4 overflow-x-auto">
        <ResultsModeTabs mode={mode} onChange={onChangeMode} />
      </div>

      {mode === "consensus" && (
        <div className="flex items-center gap-2 flex-wrap px-3 text-xs">
          <span className="text-muted-foreground">
            {t("results_refused_by_at_least")}
          </span>
          <ThresholdSegmented
            threshold={threshold}
            onChange={onChangeThreshold}
          />
          <span className="text-muted-foreground">
            {t("results_voters_label")}
          </span>
        </div>
      )}

      {mode === "blockers" && nearMisses.length === 0 && (
        <div
          className="mx-3 rounded-md border border-success/40 bg-success/[0.06] px-3 py-2 text-sm text-success"
          data-testid="blockers-empty-banner"
        >
          {t("results_no_blockers")}
        </div>
      )}

      {mode === "blockers" && dayNearMisses.length > 0 && (
        <div
          className="flex flex-col gap-2 px-3"
          data-testid="blocker-callouts"
        >
          {dayNearMisses.map((nm) => {
            const s = slotById.get(nm.slotId);
            if (!s) return null;
            return (
              <BlockerCallout
                key={nm.slotId}
                nearMiss={nm}
                slot={s}
                isShared={isShared}
                onCopyLink={onCopyLink}
              />
            );
          })}
        </div>
      )}

      <div
        data-testid="mobile-slot-cards"
        className="flex flex-col gap-2 px-3 pb-3 overflow-y-auto"
      >
        {activeSlots.map((s) => {
          const highlighted = !!highlightedSlotIds?.has(s.id);
          const dimmed = !!dimmedSlotIds?.has(s.id);
          return (
            <div
              key={s.id}
              data-highlighted={highlighted ? "true" : undefined}
              data-dimmed={dimmed ? "true" : undefined}
              className={`rounded-xl transition-opacity ${
                highlighted ? "ring-2 ring-warning/50" : ""
              } ${dimmed ? "opacity-30" : ""}`}
            >
              <SlotCard
                slot={s}
                participants={cardParticipants}
                insights={insights}
              />
            </div>
          );
        })}
        <div className="text-center text-[10px] text-muted-foreground/70 pt-1.5 pb-3">
          {t("results.mobile.swipeHint")}
        </div>
      </div>

      {mode === "consensus" && (consensus?.hiddenSlotIds.length ?? 0) > 0 && (
        <div className="px-3">
          <ConsensusBanner
            hiddenCount={consensus?.hiddenSlotIds.length ?? 0}
            onShowAll={() => onChangeThreshold(Number.POSITIVE_INFINITY)}
          />
        </div>
      )}

      {mode === "engagement" && engagement && engagement.pending.length > 0 && (
        <div className="px-3">
          <PendingParticipants
            participants={engagement.pending}
            isShared={isShared}
            onCopyLink={onCopyLink}
            onCopyShareLink={
              engagement.pending.length > 1 ? onCopyShareLink : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

export default MobileResults;
